import { z } from 'zod';
import { IsoDateTime, PageMeta, PageRequest, MutationReason } from './_common.js';
export const LiveOpsType = z.enum(['happy_hour', 'themed_event', 'banner_only', 'double_coin_weekend']);
export const LiveOpsScope = z.enum(['global', 'per_game']);
export const LiveOpsStatus = z.enum(['draft', 'scheduled', 'active', 'ended', 'cancelled']);
/**
 * Structured effects (Live-Ops Tier 0).
 *
 * Each event's payload may carry an `effects[]` array. The server reads it on hot paths
 * (finalizeFeverPool, applyAward, claimDailyBonus...) and applies any matching multipliers.
 *
 * Stacking rule: different `target`s stack independently (Heat × 1.5 + Coins × 2 → both apply);
 * same `target` from multiple concurrent events takes max() — predictable, can't double-stack.
 */
export const LiveOpsMultiplierTarget = z.enum([
    'heat_earned', // Club Heat Points contributed per fever
    'coins_won', // Win amount per spin
    'club_heat_points', // Reserved alias for heat_earned (V1.5 may diverge semantics)
    'daily_bonus', // Daily claim payout
    'drop_rate', // Frame shard / inventory drop probability (V1.5 placeholder)
]);
export const LiveOpsEffect = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('multiplier'),
        target: LiveOpsMultiplierTarget,
        factor: z.number().min(0.1).max(10),
    }),
    z.object({
        kind: z.literal('replacement'),
        /** Custom banner image (Cloudflare CDN url). Falls back to text banner if unset. */
        banner_image_url: z.string().url().nullable().optional(),
        /** Override default daily bonus contents. */
        daily_bonus_override: z.array(z.object({
            kind: z.string(),
            amount: z.number().int().positive(),
        })).optional(),
        /** Lobby color theme tag — UI maps to a Tailwind class set. */
        color_theme: z.enum(['rose', 'pink', 'red', 'gold', 'pastel', 'green', 'orange']).optional(),
    }),
]);
/**
 * Canonical payload shape that sits on `live_ops_events.payload` jsonb.
 * Old rows (pre-effects) parse with `effects: []` fallback so nothing breaks.
 */
export const LiveOpsPayload = z.object({
    effects: z.array(LiveOpsEffect).default([]),
    /** Legacy field — old happy_hour payload had `heat_growth_multiplier` only. */
    heat_growth_multiplier: z.number().optional(),
}).passthrough();
/** Player-facing active event view. Stripped of admin-only fields. */
export const LiveOpsActiveEvent = z.object({
    id: z.string(),
    type: LiveOpsType,
    scope: LiveOpsScope,
    game_ids: z.array(z.string()),
    starts_at: IsoDateTime,
    ends_at: IsoDateTime,
    banner_text: z.string().nullable(),
    banner_image_url: z.string().nullable(),
    color_theme: z.string().nullable(),
    effects: z.array(LiveOpsEffect),
});
export const LiveOpsActiveResponse = z.object({
    events: z.array(LiveOpsActiveEvent),
    server_time: IsoDateTime,
});
export const LiveOpsEvent = z.object({
    id: z.string(),
    type: LiveOpsType,
    scope: LiveOpsScope,
    game_ids: z.array(z.string()),
    payload: z.record(z.unknown()),
    starts_at: IsoDateTime,
    ends_at: IsoDateTime,
    status: LiveOpsStatus,
    banner_text_en: z.string().nullable(),
    created_by: z.string(),
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
});
/**
 * Optional recurrence — when present, the create handler expands one admin click into
 * multiple event rows (one per occurrence), each fully independent (own id, own status).
 * V1 supports weekly + daily cadences; cancelling future rows is per-row from the list.
 */
export const LiveOpsRecurrence = z.object({
    cadence: z.enum(['weekly', 'daily']),
    count: z.number().int().min(2).max(52), // up to a year of weekly Tuesdays
});
export const LiveOpsCreateRequest = z.object({
    type: LiveOpsType,
    scope: LiveOpsScope,
    game_ids: z.array(z.string()).default([]),
    payload: z.record(z.unknown()),
    starts_at: IsoDateTime,
    ends_at: IsoDateTime,
    banner_text_en: z.string().max(120).nullable().default(null),
    recurrence: LiveOpsRecurrence.optional(),
    reason: MutationReason,
});
/** Response for a recurring create — returns ALL spawned events, not just the first. */
export const LiveOpsBulkCreateResponse = z.object({
    events: z.array(LiveOpsEvent),
});
export const LiveOpsCreateResponse = LiveOpsEvent;
export const LiveOpsUpdateRequest = z.object({
    payload: z.record(z.unknown()).optional(),
    ends_at: IsoDateTime.optional(),
    banner_text_en: z.string().max(120).nullable().optional(),
    reason: MutationReason,
});
export const LiveOpsUpdateResponse = LiveOpsEvent;
export const LiveOpsListRequest = PageRequest.extend({
    status: LiveOpsStatus.optional(),
    type: LiveOpsType.optional(),
    game_id: z.string().optional(),
    starts_after: IsoDateTime.optional(),
    starts_before: IsoDateTime.optional(),
});
export const LiveOpsListResponse = z.object({
    items: z.array(LiveOpsEvent),
    page: PageMeta,
});
export const LiveOpsEndNowRequest = z.object({
    reason: MutationReason,
});
export const LiveOpsEndNowResponse = LiveOpsEvent;
export const LiveOpsCancelRequest = z.object({
    reason: MutationReason,
});
export const LiveOpsCancelResponse = LiveOpsEvent;
export const LiveOpsBannerPreviewRequest = z.object({
    type: LiveOpsType,
    banner_text_en: z.string(),
    payload: z.record(z.unknown()),
});
export const LiveOpsBannerPreviewResponse = z.object({
    rendered_html: z.string(),
    estimated_reach_users: z.number().int(),
});
//# sourceMappingURL=live_ops.js.map